import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { store, useAppStore } from '../store/appStore';
import type { ToolMode } from '../store/types';
import {
  IconArrange,
  IconBlock,
  IconCentre,
  IconDel,
  IconDrop,
  IconDup,
  IconErase,
  IconLayFlat,
  IconMove,
  IconOverhang,
  IconPaint,
  IconRotate,
  IconRedo,
  IconScale,
  IconUndo,
} from '../ui/icons';

type RailItem = {
  id: string;
  label: string;
  title: string;
  icon: ReactNode;
  active?: (s: { tool: ToolMode; overhangHighlight: boolean }) => boolean;
  disabled?: (s: { selectedId: string | null; hasObjects: boolean }) => boolean;
  onClick: () => void;
};

function RailButton({
  item,
  active,
  disabled,
}: {
  item: RailItem;
  active: boolean;
  disabled: boolean;
}) {
  const [tip, setTip] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  function show() {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setTip(true), 380);
  }
  function hide() {
    window.clearTimeout(timer.current);
    setTip(false);
  }

  return (
    <button
      type="button"
      className={`rail-btn ${active ? 'active' : ''}`}
      disabled={disabled}
      aria-label={item.label}
      title={item.title}
      onClick={item.onClick}
      onPointerDown={show}
      onPointerUp={hide}
      onPointerLeave={hide}
      onPointerCancel={hide}
    >
      {item.icon}
      <span className="rail-label">{item.label}</span>
      {tip && <span className="rail-tip">{item.title}</span>}
    </button>
  );
}

export function ToolRail() {
  const tool = useAppStore((s) => s.tool);
  const overhangHighlight = useAppStore((s) => s.overhangHighlight);
  const selectedId = useAppStore((s) => s.selectedId);
  const objects = useAppStore((s) => s.objects);
  const brushSize = useAppStore((s) => s.brushSize);
  const hasObjects = objects.length > 0;
  const ctx = { tool, overhangHighlight };
  const sel = { selectedId, hasObjects };

  const primary: RailItem[] = [
    {
      id: 'move',
      label: 'Move',
      title: 'Move',
      icon: <IconMove />,
      active: (s) => s.tool === 'move',
      onClick: () => store.setTool(tool === 'move' ? 'select' : 'move'),
    },
    {
      id: 'rotate',
      label: 'Rotate',
      title: 'Rotate',
      icon: <IconRotate />,
      active: (s) => s.tool === 'rotate',
      onClick: () => store.setTool(tool === 'rotate' ? 'select' : 'rotate'),
    },
    {
      id: 'scale',
      label: 'Scale',
      title: 'Scale',
      icon: <IconScale />,
      active: (s) => s.tool === 'scale',
      onClick: () => store.setTool(tool === 'scale' ? 'select' : 'scale'),
    },
    {
      id: 'layflat',
      label: 'Flat',
      title: 'Lay flat',
      icon: <IconLayFlat />,
      disabled: (s) => !s.selectedId,
      onClick: () => {
        const o = store.getSnapshot().objects.find((x) => x.id === store.getSnapshot().selectedId);
        if (!o) return;
        store.record('Lay flat', () => {
          store.updateObject(o.id, { rotation: [0, 0, 0] });
          store.dropSelectedToBed();
        });
      },
    },
    {
      id: 'paint',
      label: 'Paint',
      title: 'Support paint',
      icon: <IconPaint />,
      active: (s) => s.tool === 'paint-enforce',
      onClick: () => store.setTool(tool === 'paint-enforce' ? 'select' : 'paint-enforce'),
    },
    {
      id: 'block',
      label: 'Block',
      title: 'Block supports',
      icon: <IconBlock />,
      active: (s) => s.tool === 'paint-block',
      onClick: () => store.setTool(tool === 'paint-block' ? 'select' : 'paint-block'),
    },
    {
      id: 'erase',
      label: 'Erase',
      title: 'Erase paint',
      icon: <IconErase />,
      active: (s) => s.tool === 'paint-erase',
      onClick: () => store.setTool(tool === 'paint-erase' ? 'select' : 'paint-erase'),
    },
    {
      id: 'overhangs',
      label: 'Over',
      title: 'Overhangs',
      icon: <IconOverhang />,
      active: (s) => s.overhangHighlight,
      onClick: () => store.set({ overhangHighlight: !store.getSnapshot().overhangHighlight }),
    },
  ];

  const secondary: RailItem[] = [
    {
      id: 'centre',
      label: 'Centre',
      title: 'Centre',
      icon: <IconCentre />,
      disabled: (s) => !s.selectedId,
      onClick: () => store.centreSelected(),
    },
    {
      id: 'drop',
      label: 'Drop',
      title: 'Drop to bed',
      icon: <IconDrop />,
      disabled: (s) => !s.selectedId,
      onClick: () => store.dropSelectedToBed(),
    },
    {
      id: 'arrange',
      label: 'Arr.',
      title: 'Arrange',
      icon: <IconArrange />,
      disabled: (s) => !s.hasObjects,
      onClick: () => store.autoArrange(),
    },
    {
      id: 'dup',
      label: 'Dup',
      title: 'Duplicate',
      icon: <IconDup />,
      disabled: (s) => !s.selectedId,
      onClick: () => store.duplicateSelected(),
    },
    {
      id: 'del',
      label: 'Del',
      title: 'Delete',
      icon: <IconDel />,
      disabled: (s) => !s.selectedId,
      onClick: () => store.deleteSelected(),
    },
  ];

  const canUndo = useAppStore((s) => s.canUndo);
  const canRedo = useAppStore((s) => s.canRedo);

  return (
    <div className="tool-rail" data-testid="tool-rail">
      <div className="rail-history">
        <button
          type="button"
          className="rail-btn"
          data-testid="undo-btn-rail"
          title="Undo"
          aria-label="Undo"
          disabled={!canUndo}
          onClick={() => store.undo()}
        >
          <IconUndo />
          <span className="rail-label">Undo</span>
        </button>
        <button
          type="button"
          className="rail-btn"
          data-testid="redo-btn-rail"
          title="Redo"
          aria-label="Redo"
          disabled={!canRedo}
          onClick={() => store.redo()}
        >
          <IconRedo />
          <span className="rail-label">Redo</span>
        </button>
        <div className="rail-divider" />
      </div>
      {primary.map((item) => (
        <RailButton
          key={item.id}
          item={item}
          active={item.active?.(ctx) ?? false}
          disabled={item.disabled?.(sel) ?? false}
        />
      ))}
      <div className="rail-secondary">
        <div className="rail-divider" />
        {secondary.map((item) => (
          <RailButton
            key={item.id}
            item={item}
            active={item.active?.(ctx) ?? false}
            disabled={item.disabled?.(sel) ?? false}
          />
        ))}
      </div>
      {tool.startsWith('paint') && (
        <label className="rail-brush">
          <span>Brush {brushSize}mm</span>
          <input
            type="range"
            min={1}
            max={20}
            step={0.5}
            value={brushSize}
            onChange={(e) => store.set({ brushSize: parseFloat(e.target.value) })}
          />
        </label>
      )}
    </div>
  );
}
